import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, rupee, imageUrl } from "../lib/api";
import { useStores } from "../lib/stores";
import { calcPosBill } from "../lib/gst";
import { GST_STATES } from "../constants/gstStates";
import { ArrowLeft, ScanLine, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ROW_GRID =
  "grid w-full grid-cols-[48px_minmax(0,1.3fr)_68px_60px_minmax(0,0.9fr)_56px_80px_52px_44px_40px] items-center gap-x-3 px-4";

function pickProductImage(p) {
  if (Array.isArray(p.images) && p.images[0]) return p.images[0];
  if (Array.isArray(p.template_images) && p.template_images[0]) return p.template_images[0];
  return null;
}

function saleToCustomer(sale) {
  const snap = sale?.customer_snapshot || {};
  const ship = snap.shipping || {};
  return {
    billing_name: snap.name || "",
    name: snap.customer_name || snap.name || "",
    phone: snap.phone || "",
    gstin: snap.gstin || "",
    billing_address: snap.address || "",
    billing_city: snap.city || "",
    billing_state: snap.state || "",
    shipping_name: ship.name || "",
    shipping_address: ship.address || "",
    shipping_city: ship.city || "",
    shipping_state: ship.state || "",
  };
}

export default function SaleEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [barcode, setBarcode] = useState("");
  const [items, setItems] = useState([]);
  const [customer, setCustomer] = useState({
    billing_name: "",
    name: "",
    phone: "",
    gstin: "",
    billing_address: "",
    billing_city: "",
    billing_state: "",
    shipping_name: "",
    shipping_address: "",
    shipping_city: "",
    shipping_state: "",
  });
  const [addressSection, setAddressSection] = useState("billing");
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const { stores } = useStores();
  const inputRef = useRef();

  const store = stores.find((s) => s.id === sale?.store_id) || stores.find((s) => s.is_default) || stores[0];
  const allowZeroStock = store?.allow_zero_stock_bill === true;
  const priceIncludesGst = store?.product_price_includes_gst !== false;
  const storeState = store?.state || "";

  useEffect(() => {
    setLoading(true);
    api.get(`/sales/${id}`)
      .then(async (r) => {
        const data = r.data;
        if ((data.returns || []).length > 0 || data.return_status !== "active") {
          toast.error("Invoices with returns cannot be edited");
          navigate(`/app/sales/${id}`, { replace: true });
          return;
        }
        setSale(data);
        setCustomer(saleToCustomer(data));
        setDiscount(data.discount || 0);
        setNotes(data.notes || "");

        const cartItems = await Promise.all(
          (data.items || []).map(async (it) => {
            let stock = it.quantity || 0;
            try {
              const { data: prod } = await api.get(`/products/${it.product_id}`);
              stock = (prod.stock ?? 0) + (it.quantity || 0);
              return {
                product_id: it.product_id,
                name: it.product_name,
                color: prod.color || "",
                size: prod.size || "",
                image: pickProductImage(prod),
                unit_price: it.unit_price,
                quantity: it.quantity,
                discount: it.discount || 0,
                barcode: it.barcode || prod.barcode,
                stock,
                gst_percent: it.gst_percent ?? prod.gst_percent ?? 5,
                hsn_code: it.hsn_code || prod.hsn_code || "",
              };
            } catch {
              return {
                product_id: it.product_id,
                name: it.product_name,
                color: "",
                size: "",
                image: null,
                unit_price: it.unit_price,
                quantity: it.quantity,
                discount: it.discount || 0,
                barcode: it.barcode,
                stock,
                gst_percent: it.gst_percent ?? 5,
                hsn_code: it.hsn_code || "",
              };
            }
          })
        );
        setItems(cartItems);
      })
      .catch(() => {
        toast.error("Invoice not found");
        navigate("/app/sales", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const scan = async (e) => {
    e.preventDefault();
    if (!barcode) return;
    try {
      const { data: p } = await api.get(`/products/barcode/${barcode.trim()}`);
      if (p.status === "draft") {
        toast.error("Draft product — publish it before selling");
        return;
      }
      addItem(p);
      setBarcode("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Barcode not found");
    }
  };

  const addItem = (p) => {
    setItems((prev) => {
      const found = prev.find((x) => x.product_id === p.id);
      const onBill = found?.quantity || 0;
      const nextQty = onBill + 1;
      const avail = allowZeroStock ? nextQty : (p.stock ?? 0) + onBill;
      if (!allowZeroStock && nextQty > avail) {
        toast.error("Insufficient stock");
        return prev;
      }
      if (found) {
        return prev.map((x) =>
          x.product_id === p.id
            ? { ...x, quantity: x.quantity + 1, stock: (p.stock ?? 0) + x.quantity + 1 }
            : x
        );
      }
      return [
        ...prev,
        {
          product_id: p.id,
          name: p.name,
          color: p.color || "",
          size: p.size || "",
          image: pickProductImage(p),
          unit_price: p.price,
          quantity: 1,
          discount: 0,
          barcode: p.barcode,
          stock: (p.stock ?? 0) + 1,
          gst_percent: p.gst_percent ?? 5,
          hsn_code: p.hsn_code || "",
        },
      ];
    });
  };

  const removeItem = (pid) => setItems((s) => s.filter((x) => x.product_id !== pid));

  const setQty = (pid, q) => {
    const qty = Math.max(1, Number(q) || 1);
    setItems((s) => {
      const item = s.find((x) => x.product_id === pid);
      const maxAvail = allowZeroStock ? qty : item?.stock ?? qty;
      if (!allowZeroStock && item && qty > maxAvail) {
        toast.error("Insufficient stock");
        return s;
      }
      return s.map((x) => (x.product_id === pid ? { ...x, quantity: qty } : x));
    });
  };

  const bill = calcPosBill(items, discount, priceIncludesGst, storeState, customer.billing_state);
  const { grossTotal, subtotal, taxAmt, total, cgst, sgst, igst, isInterState } = bill;

  const save = async () => {
    if (items.length === 0) return toast.error("Add at least one item");
    if (!customer.billing_name?.trim()) return toast.error("Billing name is required");
    if (!customer.billing_state?.trim()) return toast.error("Billing state is required");
    setBusy(true);
    try {
      const billingName = customer.billing_name.trim();
      const { data } = await api.put(`/sales/${id}`, {
        items: items.map(({ product_id, quantity, unit_price, discount: lineDiscount }) => ({
          product_id,
          quantity,
          unit_price,
          discount: lineDiscount,
        })),
        discount: Number(discount || 0),
        billing_name: billingName,
        customer_name: customer.name?.trim() || undefined,
        customer_phone: customer.phone || undefined,
        customer_address: customer.billing_address || undefined,
        customer_city: customer.billing_city || undefined,
        customer_state: customer.billing_state,
        customer_gstin: customer.gstin?.trim() || undefined,
        shipping_name: customer.shipping_name?.trim() || undefined,
        shipping_address: customer.shipping_address || undefined,
        shipping_city: customer.shipping_city || undefined,
        shipping_state: customer.shipping_state || undefined,
        customer_id: sale?.customer_id || undefined,
        store_id: sale?.store_id || undefined,
        notes: notes.trim() || undefined,
      });
      toast.success(`Invoice ${data.invoice_no} updated`);
      navigate(`/app/sales/${id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update invoice");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center" style={{ color: "var(--text-muted)" }}>Loading invoice…</div>;
  }

  if (!sale) return null;

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="sale-edit-root">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <Link to={`/app/sales/${id}`} className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--primary)" }}>
          <ArrowLeft size={16} /> Back to invoice
        </Link>
        <div className="font-mono text-sm font-semibold" style={{ color: "var(--primary)" }}>{sale.invoice_no}</div>
      </div>

      <h1 className="font-display font-bold text-3xl mb-6">Edit invoice</h1>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          <form onSubmit={scan} className="card-soft p-4 flex items-center gap-3">
            <div className="w-11 h-11 rounded-md grid place-items-center" style={{ background: "var(--surface-success)", color: "var(--success)" }}>
              <ScanLine size={22} />
            </div>
            <input
              ref={inputRef}
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              placeholder="Scan barcode to add more items"
              className="flex-1 h-12 px-3 rounded-md border bg-white text-lg num"
              style={{ borderColor: "var(--border-soft)" }}
            />
            <button type="submit" className="h-12 px-5 rounded-md text-white font-semibold tactile-btn" style={{ background: "var(--primary)" }}>
              Add
            </button>
          </form>

          <div className="card-soft">
            <div className={`${ROW_GRID} py-3 text-xs font-medium uppercase tracking-wide`} style={{ color: "var(--text-muted)" }}>
              <div>Image</div>
              <div>Product</div>
              <div>Color</div>
              <div>Size</div>
              <div>Barcode</div>
              <div className="text-center">HSN</div>
              <div className="text-right">Price</div>
              <div className="text-center">Qty</div>
              <div className="text-center">GST</div>
              <div />
            </div>

            {items.length === 0 && (
              <div className="text-center py-12 text-sm" style={{ color: "var(--text-muted)" }}>
                Add at least one line item.
              </div>
            )}

            {items.map((it) => (
              <div key={it.product_id} className={`${ROW_GRID} py-3 border-t text-sm`} style={{ borderColor: "var(--border-soft)" }}>
                <div>
                  {it.image ? (
                    <img src={imageUrl(it.image)} alt="" className="w-10 h-10 rounded-md border object-cover" style={{ borderColor: "var(--border-soft)" }} />
                  ) : (
                    <div className="w-10 h-10 rounded-md border" style={{ borderColor: "var(--border-soft)", background: "var(--surface-muted)" }} />
                  )}
                </div>
                <div className="font-medium leading-snug pr-2">{it.name}</div>
                <div className="text-sm truncate" style={{ color: "var(--text-muted)" }}>{it.color || "—"}</div>
                <div className="font-semibold">{it.size || "—"}</div>
                <div className="font-mono text-xs truncate">{it.barcode}</div>
                <div className="text-center font-mono text-xs truncate" style={{ color: "var(--text-muted)" }}>{it.hsn_code || "—"}</div>
                <div className="text-right num whitespace-nowrap">{rupee(it.unit_price)}</div>
                <div className="flex justify-center">
                  <input
                    type="number"
                    min="1"
                    value={it.quantity}
                    onChange={(e) => setQty(it.product_id, e.target.value)}
                    className="w-14 h-10 px-2 border rounded-md num text-center"
                    style={{ borderColor: "var(--border-soft)" }}
                  />
                </div>
                <div className="text-center num font-medium">{it.gst_percent ?? 5}%</div>
                <div className="flex justify-end">
                  <button type="button" onClick={() => removeItem(it.product_id)} style={{ color: "var(--error)" }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="card-soft p-4">
            <label className="text-sm font-medium" htmlFor="edit-notes">Notes</label>
            <textarea
              id="edit-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-2 w-full px-3 py-2.5 rounded-md border bg-white text-sm resize-y min-h-[5.5rem]"
              style={{ borderColor: "var(--border-soft)" }}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="card-soft p-4">
            <div className="font-display font-semibold mb-3">Customer details</div>
            <div className="space-y-3">
              <input placeholder="Phone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="h-12 w-full px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }} />
              <input placeholder="Customer name" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="h-12 w-full px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }} />
              <div>
                <label className="text-sm font-medium">Billing name *</label>
                <input required value={customer.billing_name} onChange={(e) => setCustomer({ ...customer, billing_name: e.target.value })} className="mt-1 h-12 w-full px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }} />
              </div>
              <input placeholder="GSTIN" value={customer.gstin} onChange={(e) => setCustomer({ ...customer, gstin: e.target.value.toUpperCase().replace(/\s/g, "") })} className="h-12 w-full px-3 rounded-md border bg-white font-mono text-sm" style={{ borderColor: "var(--border-soft)" }} />
              <div className="flex gap-4 pt-1">
                <label className="inline-flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input type="radio" checked={addressSection === "billing"} onChange={() => setAddressSection("billing")} />
                  Billing
                </label>
                <label className="inline-flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input type="radio" checked={addressSection === "shipping"} onChange={() => setAddressSection("shipping")} />
                  Shipping
                </label>
              </div>
              {addressSection === "billing" ? (
                <>
                  <input placeholder="Billing address" value={customer.billing_address} onChange={(e) => setCustomer({ ...customer, billing_address: e.target.value })} className="h-12 w-full px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }} />
                  <input placeholder="Billing city" value={customer.billing_city} onChange={(e) => setCustomer({ ...customer, billing_city: e.target.value })} className="h-12 w-full px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }} />
                  <select required value={customer.billing_state} onChange={(e) => setCustomer({ ...customer, billing_state: e.target.value })} className="h-12 w-full px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }}>
                    <option value="">Select state</option>
                    {GST_STATES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </>
              ) : (
                <>
                  <input placeholder="Shipping name" value={customer.shipping_name} onChange={(e) => setCustomer({ ...customer, shipping_name: e.target.value })} className="h-12 w-full px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }} />
                  <input placeholder="Shipping address" value={customer.shipping_address} onChange={(e) => setCustomer({ ...customer, shipping_address: e.target.value })} className="h-12 w-full px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }} />
                  <input placeholder="Shipping city" value={customer.shipping_city} onChange={(e) => setCustomer({ ...customer, shipping_city: e.target.value })} className="h-12 w-full px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }} />
                  <select value={customer.shipping_state} onChange={(e) => setCustomer({ ...customer, shipping_state: e.target.value })} className="h-12 w-full px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }}>
                    <option value="">Select state</option>
                    {GST_STATES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </>
              )}
            </div>
          </div>

          <div className="card-soft p-4">
            <div className="font-display font-semibold mb-3">Bill summary</div>
            <div className="flex justify-between text-sm py-1">
              <span>Total amount</span>
              <span className="num">{rupee(grossTotal)}</span>
            </div>
            <div className="flex items-center justify-between text-sm py-1">
              <span>Discount ₹</span>
              <input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-24 h-9 text-right px-2 border rounded-md num" style={{ borderColor: "var(--border-soft)" }} />
            </div>
            <div className="flex justify-between text-sm py-1">
              <span>{priceIncludesGst ? "Taxable amount" : "Subtotal"}</span>
              <span className="num">{rupee(subtotal)}</span>
            </div>
            {isInterState ? (
              <div className="flex justify-between text-sm py-1">
                <span>IGST</span>
                <span className="num">{rupee(igst)}</span>
              </div>
            ) : (
              <>
                <div className="flex justify-between text-sm py-1"><span>CGST</span><span className="num">{rupee(cgst)}</span></div>
                <div className="flex justify-between text-sm py-1"><span>SGST</span><span className="num">{rupee(sgst)}</span></div>
              </>
            )}
            <div className="flex justify-between text-sm py-1" style={{ color: "var(--text-muted)" }}>
              <span>Total GST</span>
              <span className="num">{rupee(taxAmt)}</span>
            </div>
            <hr className="my-3" style={{ borderColor: "var(--border-soft)" }} />
            <div className="flex justify-between items-baseline">
              <span className="font-display font-semibold">Grand total</span>
              <span className="font-display font-bold text-3xl num" style={{ color: "var(--primary)" }}>{rupee(total)}</span>
            </div>

            <button
              type="button"
              disabled={busy || items.length === 0}
              onClick={save}
              className="mt-4 w-full h-14 rounded-md text-white font-display font-bold text-lg tactile-btn"
              style={{ background: "var(--primary)" }}
              data-testid="sale-edit-save"
            >
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
